import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast } from "sonner";
import { Save, Building2, User, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useBilling } from "../context/BillingContext";
import ProfileSuggestions from "../components/ProfileSuggestions";
import { WebsiteDraft } from "../components/WebsiteDraft";

const F = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">{label}</label>
    <div className="mt-2">{children}</div>
  </div>
);

export default function Profile() {
  const { user } = useAuth();
  const { orgType, setOrg, label } = useSettings();
  const { isOrgMember, handleApiError } = useBilling();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    const { data } = await api.get("/profile");
    if (!Array.isArray(data.values)) data.values = [];
    setP(data);
    const id = window.location.hash.slice(1);
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 150);
  })(); }, []);

  const update = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...p, values: typeof p.values === "string"
        ? p.values.split(",").map(s=>s.trim()).filter(Boolean) : p.values };
      await api.put("/profile", payload);
      toast.success(`${label("profile")} saved`);
    } catch (e) { if (!handleApiError(e)) toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!p) return <AppShell title={label("profile")}><div className="text-zinc-500">Loading…</div></AppShell>;

  const inp = "w-full px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#0033FF] text-sm bg-white";
  const ta = inp + " min-h-[110px] leading-relaxed";

  const isOrg = orgType === "organization";
  const entityWord = isOrg ? "organization" : "company";

  return (
    <AppShell eyebrow="The DNA" title={label("profile")}
      actions={
        <button data-testid="profile-save-btn" onClick={save} disabled={saving}
          className="bg-[#0033FF] text-white px-5 py-3 text-xs font-bold tracking-wider uppercase hover:bg-zinc-900 disabled:opacity-50 flex items-center gap-2">
          <Save className="w-3.5 h-3.5" />{saving ? "Saving…" : "Save profile"}
        </button>
      }>

      {/* Org type selector */}
      <div className="border border-zinc-200 p-4 mb-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 bg-zinc-50">
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-700">I lead a</div>
        <div className="flex gap-1">
          <button data-testid="org-type-company" onClick={()=>setOrg("company")}
            className={`px-4 py-2 text-xs font-bold tracking-wider uppercase border ${orgType==="company" ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:border-zinc-900"}`}>
            For-profit company
          </button>
          <button data-testid="org-type-organization" onClick={()=>setOrg("organization")}
            className={`px-4 py-2 text-xs font-bold tracking-wider uppercase border ${orgType==="organization" ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:border-zinc-900"}`}>
            Non-profit organization
          </button>
        </div>
        <div className="text-xs text-zinc-500 sm:ml-auto">Labels across the app auto-adjust to match.</div>
      </div>

      <p className="text-sm text-zinc-600 max-w-2xl mb-8">
        Everything VizionCast knows about you and your {entityWord}. The richer this gets, the more your generated content sounds like you.
      </p>

      {isOrgMember && <ProfileSuggestions />}

      {/* ============ COMPANY / ORGANIZATION SECTION ============ */}
      <section id="company" className="mb-12 scroll-mt-24">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#0033FF] flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#0033FF] font-bold">Section 01</div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tighter">{isOrg ? "Organization" : "Company"} profile</h2>
          </div>
          {!isOrgMember && <div className="ml-auto"><WebsiteDraft current={p} onApply={(d) => setP((prev) => ({ ...prev, ...d }))} /></div>}
        </div>
        <div className="swiss-rule mb-6"></div>

        <div className="border border-zinc-200 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200">
          <div className="bg-white p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label={`${isOrg ? "Organization" : "Company"} name`}><input data-testid="profile-company-name" className={inp} value={p.company_name||""} onChange={(e)=>update("company_name", e.target.value)}/></F>
            <F label="Industry / Cause"><input data-testid="profile-industry" className={inp} value={p.industry||""} onChange={(e)=>update("industry", e.target.value)}/></F>
            <F label="Tagline"><input data-testid="profile-tagline" className={inp} value={p.tagline||""} onChange={(e)=>update("tagline", e.target.value)}/></F>
            <F label="Motto"><input data-testid="profile-motto" className={inp} value={p.motto||""} onChange={(e)=>update("motto", e.target.value)}/></F>
            <F label="Founding date" full><input data-testid="profile-founding" className={inp} value={p.founding_date||""} onChange={(e)=>update("founding_date", e.target.value)} placeholder="e.g. March 2019"/></F>
          </div>
          <div className="bg-white p-6 space-y-4">
            <F label="Mission"><textarea data-testid="profile-mission" className={ta} value={p.mission||""} onChange={(e)=>update("mission", e.target.value)}/></F>
            <F label="Vision"><textarea data-testid="profile-vision" className={ta} value={p.vision||""} onChange={(e)=>update("vision", e.target.value)}/></F>
            <F label="Values (comma separated)">
              <input data-testid="profile-values" className={inp}
                value={Array.isArray(p.values) ? p.values.join(", ") : p.values}
                onChange={(e)=>update("values", e.target.value)}/>
            </F>
          </div>
          <div className="bg-white p-6 md:col-span-2">
            <F label="Origin story"><textarea data-testid="profile-origin" className={ta + " min-h-[160px]"} value={p.origin_story||""} onChange={(e)=>update("origin_story", e.target.value)} placeholder={`How did this ${entityWord} come to be? What problem ignited it?`}/></F>
          </div>
          <div className="bg-white p-6 md:col-span-2 border-l-4 border-[#0033FF]">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-[#0033FF]" />
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#0033FF]">The North Star prompt</div>
            </div>
            <F label={`When your ${entityWord} is at its best, what is it doing? What change is being made in the world?`}>
              <textarea data-testid="profile-best-moment" className={ta + " min-h-[160px]"}
                value={p.best_moment||""}
                onChange={(e)=>update("best_moment", e.target.value)}
                placeholder="Paint the picture. The moment, the people, the change. VizionCast will reach for this when it writes."/>
            </F>
          </div>
        </div>
      </section>

      {/* ============ LEADER SECTION ============ */}
      <section id="personal" className="scroll-mt-24">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-zinc-900 flex items-center justify-center">
            <User className="w-5 h-5 text-[#FFB800]" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-900 font-bold">Section 02</div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tighter">Leader profile · You</h2>
          </div>
        </div>
        <div className="swiss-rule mb-6"></div>

        <div className="border border-zinc-200 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200">
          <div className="bg-white p-6 space-y-4">
            <F label="Tone"><input data-testid="profile-tone" className={inp} value={p.tone||""} onChange={(e)=>update("tone", e.target.value)} placeholder="e.g. warm, direct, contrarian"/></F>
            <F label="Voice"><input data-testid="profile-voice" className={inp} value={p.voice||""} onChange={(e)=>update("voice", e.target.value)} placeholder="e.g. founder-as-friend, expert-as-mentor"/></F>
            <F label="Target audience"><input data-testid="profile-audience" className={inp} value={p.target_audience||""} onChange={(e)=>update("target_audience", e.target.value)} placeholder="Who are you talking to?"/></F>
            <F label="Your personal why">
              <textarea data-testid="profile-personal-why" className={ta} value={p.leader_personal_why||""} onChange={(e)=>update("leader_personal_why", e.target.value)} placeholder="The one sentence that pulls you out of bed."/>
            </F>
          </div>
          <div className="bg-white p-6 space-y-4">
            <F label="A short bio (so VizionCast writes in your voice)">
              <textarea data-testid="profile-leader-bio" className={ta + " min-h-[140px]"} value={p.leader_bio||""} onChange={(e)=>update("leader_bio", e.target.value)} placeholder={`I'm ${user?.name || "—"}, currently building ___. Background in ___. I write about ___.`}/>
            </F>
            <F label="Sample writings (paste a few of your favorite paragraphs)">
              <textarea data-testid="profile-samples" className={ta + " min-h-[180px]"} value={p.sample_writings||""} onChange={(e)=>update("sample_writings", e.target.value)} placeholder="Paste 1-3 emails, posts, or paragraphs that sound like you."/>
            </F>
          </div>
        </div>
      </section>

      {/* Admin inbox of team suggestions */}
      <ProfileSuggestions />
    </AppShell>
  );
}
